import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSelector } from 'react-redux';
import UserDropdown from '../UserDropdown/UserDropdown';
import './NavBar.css';
import { fetchPublicFeatureFlags } from '../../services/featureFlagsApi';
import { getDevLoyaltyEnabledOverride } from '../../utils/devOverrides';
import { loyaltySession } from '../../utils/session';
import { useInactiveRequestNotification } from '../../hooks/useInactiveRequestNotification';
import { FEATURE_FLAGS, normalizeFeatureFlags, isPublicFeatureFlagEnabled } from '../../constants/featureFlags';
import { isShishaMaster, resolveStaffRoles } from '../../utils/staffRoles';

const NavBar = ({ menuOpen, setMenuOpen, showAdminLinks = true }) => {
  const { t } = useTranslation();
  const { isAuthenticated, logout } = useAuth();
  const authState = useSelector((state) => state.auth);
  const roles = resolveStaffRoles(authState);
  const isAdmin = roles.includes('admin');
  const { hasPendingRequest, pendingCount, requestedByName } = useInactiveRequestNotification(isAdmin);
  const [loyaltyUser, setLoyaltyUser] = useState(null);
  const [isLoyaltyEnabled, setIsLoyaltyEnabled] = useState(false);

  const navigate = useNavigate();

  // Check for logged-in loyalty user
  useEffect(() => {
    const storedUser = loyaltySession.getUser();
    if (storedUser) {
      setLoyaltyUser(storedUser);
    }
  }, []);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  }

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };
  // Determine the effective role for menu rendering. Admin has highest precedence.
  const effectiveRole = (() => {

    if (!roles || !Array.isArray(roles)) return null;
    if (roles.includes('admin')) return 'admin';
    if (roles.includes('waiter')) return 'waiter';
    if (isShishaMaster(roles)) return 'shisha_master';
    return null;
  })();

  const menus = {
    admin: [
      { to: '/admin/hookah-flavors', label: 'Hookah Flavors' },
      { to: '/admin/users', label: 'Users' },
      { to: '/admin/roles', label: 'Roles' },
      { to: '/admin/brands', label: 'Brands' },
      { to: '/admin/categories', label: 'Categories' },
      { to: '/admin/drinks', label: 'Drinks' },
      { to: '/admin/scan-qr', label: 'Scan Loyalty QR' },
      ...(loyaltyUser ? [
        { to: '/loyalty/settings', label: t('nav.loyaltyGreeting', { name: loyaltyUser.name?.split(' ')[0] || loyaltyUser.name }) }
      ] : [
        { to: '/loyalty/login', label: t('nav.loyalty') }
      ])
    ],
    waiter: [
      { to: '/admin/loyalty-reservations', label: t('nav.reservations', 'Table Reservations') },
      { to: '/admin/bar-tables', label: t('nav.tables', 'Tables') },
      { to: '/admin/scan-qr', label: t('nav.scanQr') },
    ],
    shisha_master: [
      { to: '/admin/hookah-flavors', label: 'Hookah Flavors' },
      { to: '/admin/hookah-inventory', label: 'Inventory' },
      { to: '/admin/inactive-flavours-request', label: 'Inactive Flavours Request' }
    ],
    default: [
    ]
  };

  const items = (isAuthenticated && showAdminLinks) ? (menus[effectiveRole] || menus.default) : menus.default;

  // If the admin sidebar is shown elsewhere (showAdminLinks === false for admins),
  // remove the 'Hookah Flavors' top-link for admin users because it's already present
  // in the left sidebar. Keep it for other logged-in roles.
  let visibleItems = items;
  if (effectiveRole === 'admin' && isAuthenticated && !showAdminLinks) {
    visibleItems = (items || []).filter(i => i.to !== '/admin/hookah-flavors');
  }

  // (Removed admin-configured per-role Inventory visibility — feature no longer used)

  // Feature flag: show_inventory_page controls whether shisha_master (non-admin) sees Inventory.
  // Default to false to prevent brief flashing of the link before feature flags load.
  const [showInventoryFlag, setShowInventoryFlag] = useState(false);
  const [loyaltyReservationsEnabled, setLoyaltyReservationsEnabled] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const devOverride = getDevLoyaltyEnabledOverride();
        if (devOverride !== null) {
          if (mounted) setIsLoyaltyEnabled(devOverride);
        }

        // Public flags work for all staff; admin /feature-flags is admin-only (403 for nargelists).
        const publicFlags = normalizeFeatureFlags(await fetchPublicFeatureFlags());

        if (!mounted) return;

        setShowInventoryFlag(isPublicFeatureFlagEnabled(publicFlags, FEATURE_FLAGS.SHOW_INVENTORY_PAGE));

        if (devOverride === null) {
          const loyaltyFlag = publicFlags.find((f) => f.key === FEATURE_FLAGS.LOYALTY_ENABLED);
          setIsLoyaltyEnabled(loyaltyFlag?.enabled === true || loyaltyFlag?.value === true);
        }

        setLoyaltyReservationsEnabled(
          isPublicFeatureFlagEnabled(publicFlags, FEATURE_FLAGS.LOYALTY_USER_RESERVATIONS),
        );
      } catch {
        if (!mounted) return;
        setShowInventoryFlag(false);
        setLoyaltyReservationsEnabled(false);
        if (getDevLoyaltyEnabledOverride() === null) {
          setIsLoyaltyEnabled(false);
        }
      }
    })();
    return () => { mounted = false; };
  }, [isAuthenticated]);

  try {
    // Only show Inventory to non-admin shisha_master users when the flag is explicitly true.
    if (!isAdmin && isShishaMaster(roles) && !showInventoryFlag) {
      visibleItems = (visibleItems || []).filter(i => i.to !== '/admin/hookah-inventory');
    }
    
    // Public loyalty member links — hide when the loyalty program is off.
    if (!isLoyaltyEnabled) {
      visibleItems = (visibleItems || []).filter(
        (i) => !['/loyalty/login', '/loyalty/settings'].includes(i.to),
      );
    }

    // Staff scan QR requires the loyalty program.
    if (!isLoyaltyEnabled) {
      visibleItems = (visibleItems || []).filter((i) => i.to !== '/admin/scan-qr');
    }

    // Staff reservation queue: admins always; waiters only when the flag is on.
    if (!isAdmin && !loyaltyReservationsEnabled) {
      visibleItems = (visibleItems || []).filter((i) => i.to !== '/admin/loyalty-reservations');
    }
  } catch (e) {
    // ignore
  }

  return (
    <nav className="navbar">
      <Link to="/admin/hookah-flavors" className="navbar-brand">Shisha Vibes</Link>

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        {/* Notification Icon - Only for admin */}
        {isAuthenticated && isAdmin && hasPendingRequest && (
          <Tooltip title={requestedByName ? `Пусната от ${requestedByName}` : 'Има заявка за деактивиране'}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#ffd166', fontSize: 13 }}>
          {requestedByName ? `Пусната от ${requestedByName}` : null}
          <IconButton
            onClick={() => navigate('/admin/inactive-flavours-request')}
            sx={{
              color: '#ffd166',
              '&:hover': {
                backgroundColor: 'rgba(255, 107, 0, 0.1)',
              },
            }}
          >
            <Badge
              badgeContent={pendingCount}
              color="error"
              sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: '#ff6b00',
                  color: '#fff',
                }
              }}
            >
              <NotificationsIcon />
            </Badge>
          </IconButton>
          </span>
          </Tooltip>
        )}

        <button
          className="navbar-toggle"
          onClick={toggleMenu}
          aria-expanded={menuOpen}
          aria-label="Toggle navigation menu"
        >
          <MenuIcon style={{ fontSize: 32, color: '#fff' }} />
        </button>
      </div>

      <div className={`navbar-menu${menuOpen ? ' active' : ''}`}>
        {menuOpen && (
          <button className="navbar-close" onClick={toggleMenu} aria-label="Close menu">&times;</button>
        )}

        {visibleItems && visibleItems.map(item => (
          <Link key={item.to} to={item.to} className="nav-link" onClick={() => setMenuOpen(false)}>{item.label}</Link>
        ))}

        {isAuthenticated && effectiveRole !== 'admin' && (
          <button onClick={async () => { await handleLogout(); setMenuOpen(false); }} className="nav-link logout-button">Logout</button>
        )}
      </div>
    </nav>
  );
};

export default NavBar;